import {useDrag, useDrop} from "react-dnd";
import {Stack} from "@mui/material";
import {useEffect, useRef} from "react";
import {getEmptyImage} from "react-dnd-html5-backend";

type DraggableProps = {
  type: string;
  field: string;
  index: number;
  label: string;
  disabled?: boolean;
  children: React.ReactNode;
  onMove: (dragIndex: number, hoverIndex: number) => void;
};

const Draggable = ({
  type,
  field,
  index,
  label,
  disabled,
  children,
  onMove,
}: DraggableProps) => {
  const ref = useRef<HTMLDivElement>(null);

  // Drag
  const [{isDragging}, drag, preview] = useDrag({
    type,
    item: {label, index},
    canDrag: !disabled,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Drop
  const [{handlerId}, drop] = useDrop({
    accept: type,
    hover: (item: {label: string; index: number}) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      onMove(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
    collect: monitor => ({
      handlerId: monitor.getHandlerId(),
    }),
  });

  drag(drop(ref));

  useEffect(() => {
    preview(getEmptyImage(), {captureDraggingState: true});
  }, []);

  return (
    <Stack
      direction={"row"}
      sx={{position: "relative"}}
      data-handler-id={handlerId}>
      <Stack
        key={"sort_" + field}
        ref={ref}
        sx={{
          opacity: isDragging ? 0.5 : 1,
          cursor: disabled ? "default" : "grab",
        }}>
        {children}
      </Stack>
    </Stack>
  );
};

export {Draggable};
